import { forwardRef } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

interface EditIconButtonProps extends Omit<ButtonProps, 'children'> {
  tooltip?: string;
  showTooltip?: boolean;
}

/**
 * Standardized edit icon button with primary color on hover.
 */
export const EditIconButton = forwardRef<HTMLButtonElement, EditIconButtonProps>(
  ({ tooltip, showTooltip = true, className, disabled, ...props }, ref) => {
    const { t } = useTranslation('common');
    const label = tooltip || t('app.edit');

    const button = (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        aria-label={label}
        className={cn('hover:bg-primary/10 hover:text-primary', className)}
        disabled={disabled}
        {...props}
      >
        <Pencil className="h-4 w-4" />
      </Button>
    );

    if (!showTooltip) return button;

    // When disabled, wrap in span to capture pointer events for tooltip
    if (disabled) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block" tabIndex={0}>
              <span style={{ pointerEvents: 'none', display: 'inline-block' }}>
                {button}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  }
);

EditIconButton.displayName = 'EditIconButton';
